/* eslint-disable no-unused-vars */
import React, { Ref, useMemo, useState } from 'react';
import {
  Flex,
  ConfigProvider,
  Tooltip,
  Popconfirm,
  Table,
  TableColumnProps,
  Tag,
  theme,
  Segmented,
  Button,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  CompassOutlined,
  DeleteOutlined,
  LoadingOutlined,
  UserDeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PaqueryTable from '@paquery-team/lib-table';
import SizeIndicator from 'components/sizeIndicator';
import { useSelector } from 'react-redux';
import { authPaquerypointSelector } from 'redux/auth/selectors';
import { selectGlobals } from 'redux/globals/selectors';
import {
  useRemoveShipment,
  useUnassignShipmentDriver,
} from 'services/journeys';
import { useDriverPackages } from 'services/DriverService';
import {
  Action,
  ActionType,
  JourneyState,
  Shipment,
  ActionStatus,
  GeolocationType,
  Waypoint,
} from 'interfaces/journey';
import { useJourneyDashboardContext } from '../context';
import {
  getWaypointIssue,
  issueMinutes,
} from 'services/journeys/shipmentHelpers';
import { GlobalEnum } from 'interfaces/globals';
import useStylesGenerateRoute from '../styles';
import {
  isContactPaquerStatus,
  isVisitStatus,
  PACKAGE,
} from 'constants/packages';
import CollectionsTable, { TableCollection } from './CollectionsTable';
import CollectionIntakeModal from './CollectionIntakeModal';
import { issueColor } from './issueColor';

const ELLIPSIS: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Solo envíos: las colectas tienen su propia tabla, con sus columnas.
interface TableShipment extends Shipment {
  actionType: ActionType;
  statusDescription: string;
  actionStatus?: ActionStatus;
}

// Por qué una colecta sale fuera de hora: la llegada estimada contra el turno. Mismo formato que
// la columna Hora de la tabla de colectas.
const lateCollectionText = (record: TableWaypoint) => {
  const row =
    record.collections?.find((c) => c.issue) ?? record.collections?.[0];
  const start = row?.collection?.scheduledStart;
  if (!record.eta || !start) return undefined;
  return `Según el recorrido llega a las ${dayjs
    .unix(record.eta)
    .format('HH:mm')} y el turno es a las ${dayjs(start).format('HH:mm')}.`;
};

const issueStyle = (
  shipment: TableShipment,
): React.CSSProperties | undefined =>
  shipment.issue ? { color: issueColor(shipment.issue.blame) } : undefined;

interface TableWaypoint extends Waypoint {
  // La salida y el regreso: filas como las demás, sin acciones.
  endpoint?: 'start' | 'end';
  shipments?: TableShipment[];
  collections?: TableCollection[];
  canceled?: boolean;
}

const ACTION_STATUS_DESCRIPTOR: Record<
  ActionStatus,
  { label: string; icon?: React.ReactNode }
> = {
  [ActionStatus.Pending]: { label: 'Pendiente' },
  [ActionStatus.Completed]: {
    label: 'Completada',
    icon: <CheckOutlined style={{ color: 'green', fontSize: 18 }} />,
  },
  [ActionStatus.Failed]: {
    label: 'Visita',
    icon: <CloseOutlined style={{ color: 'red', fontSize: 18 }} />,
  },
  [ActionStatus.Forced_end]: { label: '-' },
  [ActionStatus.Canceled]: {
    label: 'Cancelada',
    icon: <CloseOutlined style={{ color: 'red', fontSize: 18 }} />,
  },
  [ActionStatus.Transferred]: { label: 'Transferida' },
};

const ActionsTable = ({
  shipments,
  collections,
  endpoint,
  centerDeliveries,
  id: waypointId,
}: TableWaypoint) => {
  const { state, id, driver, refetch } = useJourneyDashboardContext();
  const [intakeId, setIntakeId] = useState<number | null>(null);
  const globals = useSelector(selectGlobals);
  const { token } = theme.useToken();
  const classes = useStylesGenerateRoute({ theme: token });
  const { mutate: removePackage, isPending } = useRemoveShipment();
  const { mutate: unassignDriver, isPending: isUnassigning } =
    useUnassignShipmentDriver();
  const { data: driverPackages } = useDriverPackages(
    state === JourneyState.Finished ? driver?.id : undefined,
  );
  const [toRemove, setToRemove] = useState<number | null>(null);
  const [toUnassign, setToUnassign] = useState<number | null>(null);

  const driverPackageIds = new Set<number>(
    (driverPackages ?? []).map((packet: Shipment) => packet.id),
  );

  const hasShipments = !!shipments && shipments.length > 0;
  const collectionsTable = collections?.length ? (
    <CollectionsTable collections={collections} />
  ) : null;

  // Salida y regreso: sin acciones. Una línea de texto plano, no el bloque de las paradas,
  // que acá parecía un botón.
  if (endpoint) {
    const total = centerDeliveries?.total ?? 0;
    const received = centerDeliveries?.received ?? 0;
    return (
      <div
        style={{
          padding: '4px 16px 8px',
          color: '#595959',
        }}
      >
        {endpoint === 'start'
          ? 'Desde acá sale la ruta.'
          : !centerDeliveries
            ? 'Regreso al centro de distribución.'
            : total === 0
              ? 'Regreso al centro de distribución. No quedan colectas para entregar.'
              : `Regreso al centro de distribución: ${total} ${
                  total === 1
                    ? 'colecta para entregar'
                    : 'colectas para entregar'
                } · ${received} ya ${received === 1 ? 'ingresada' : 'ingresadas'}.`}
        {!!centerDeliveries?.carried.length && (
          <Flex vertical gap={4} style={{ marginTop: 8 }}>
            {centerDeliveries.carried.map((carried) => (
              <Flex
                key={carried.collectionId}
                align="center"
                justify="space-between"
                gap={8}
              >
                <span style={ELLIPSIS}>
                  {carried.storeName} · viene de la {carried.fromJourneyId}
                </span>
                {carried.received ? (
                  <Flex gap={8} align="center">
                    <CheckOutlined style={{ color: 'green', fontSize: 18 }} />
                    Ingresada
                  </Flex>
                ) : (
                  <Button
                    size="small"
                    onClick={() => setIntakeId(carried.collectionId)}
                  >
                    Ingresar
                  </Button>
                )}
              </Flex>
            ))}
            <CollectionIntakeModal
              collectionId={intakeId}
              onClose={() => {
                setIntakeId(null);
                refetch();
              }}
            />
          </Flex>
        )}
      </div>
    );
  }

  const driverHasShipment = (record: TableShipment) =>
    driverPackageIds.has(record.id) &&
    (record.status === PACKAGE.STATUSES.INHANDSOFPAQUER ||
      isVisitStatus(record.status));

  // Una parada puede tener las dos cosas en la misma dirección: se muestran las dos
  // tablas, cada una con sus columnas.
  if (!hasShipments) return collectionsTable;

  const externalCodeColumn: TableColumnProps<TableShipment> = {
    title: 'Envío',
    // ponytail: sin width fija, absorbe el espacio restante y se achica antes que el resto
    ellipsis: true,
    render: (shipment: TableShipment) => {
      const style =
        shipment.actionStatus === ActionStatus.Canceled
          ? { textDecoration: 'line-through', color: 'darkgray' }
          : issueStyle(shipment);
      return <p style={style}>{shipment.externalCode}</p>;
    },
    sorter: (a, b) =>
      (a.externalCode ?? '').localeCompare(b.externalCode ?? ''),
  };

  const statusColumn: TableColumnProps<TableShipment> = {
    title: 'Estado',
    width: 170,
    ellipsis: true,
    render: (shipment: TableShipment) => (
      <span style={issueStyle(shipment)}>
        {shipment.statusDescription || '-'}
      </span>
    ),
    sorter: (a, b) =>
      (a.statusDescription ?? '').localeCompare(b.statusDescription ?? ''),
  };

  const actionStatusColumn: TableColumnProps<TableShipment> = {
    title: 'Acción',
    width: 130,
    dataIndex: 'actionStatus',
    render: (actionStatus: ActionStatus, shipment: TableShipment) => {
      const descriptor = ACTION_STATUS_DESCRIPTOR[actionStatus];
      if (!descriptor) return null;
      return (
        <Flex gap={8} align="center" style={issueStyle(shipment)}>
          {descriptor.icon}
          {descriptor.label}
        </Flex>
      );
    },
    sorter: (a, b) =>
      (a.actionStatus ?? '').localeCompare(b.actionStatus ?? ''),
  };

  const sizeColumn: TableColumnProps<TableShipment> = {
    title: 'Tamaño',
    dataIndex: 'packageSize',
    align: 'center',
    render: (sizeId: number) => (
      <SizeIndicator
        text={
          globals.packages.size.find(
            (packSize: GlobalEnum) => packSize.value === sizeId,
          )?.name?.[0] ??
          globals.packages.size[0]?.name?.[0] ??
          ''
        }
      />
    ),
    width: 95,
    sorter: (a, b) => (a.packageSize ?? 0) - (b.packageSize ?? 0),
  };

  const deleteColumn: TableColumnProps<TableShipment> = {
    align: 'center',
    render: (record: TableShipment) => {
      return (
        <Popconfirm
          title="Remover"
          description="¿Desea remover el paquete de la ruta?"
          onConfirm={() => {
            if (id && record.id && record.externalCode) {
              setToRemove(record.id);
              removePackage({
                journeyId: id,
                shipmentId: record.id,
                externalCode: record.externalCode,
              });
            }
          }}
          okText="Remover"
          cancelText="Cancelar"
          placement="topRight"
          icon={<DeleteOutlined style={{ color: 'red' }} />}
          disabled={isPending || isContactPaquerStatus(record.status)}
        >
          <Tooltip
            title={
              isContactPaquerStatus(record.status)
                ? 'Debe liberar el paquete para remover'
                : 'Remover'
            }
          >
            {isPending && toRemove === record.id ? (
              <LoadingOutlined style={{ color: 'grey', fontSize: 18 }} />
            ) : (
              <DeleteOutlined
                style={
                  isContactPaquerStatus(record.status)
                    ? { color: 'darkgray', fontSize: 18, cursor: 'not-allowed' }
                    : { color: 'red', fontSize: 18 }
                }
              />
            )}
          </Tooltip>
        </Popconfirm>
      );
    },
    width: 50,
  };

  const unassignDriverColumn: TableColumnProps<TableShipment> = {
    align: 'center',
    render: (record: TableShipment) => {
      const canUnassign =
        record.actionStatus !== ActionStatus.Completed &&
        driverHasShipment(record);
      const loading = isUnassigning && toUnassign === record.id;
      return (
        <Popconfirm
          title="Desasignar driver"
          description="¿Desea desasignar el driver del envío?"
          onConfirm={() => {
            if (id && record.id) {
              setToUnassign(record.id);
              unassignDriver({ journeyId: id, shipmentId: record.id });
            }
          }}
          okText="Desasignar"
          cancelText="Cancelar"
          placement="topRight"
          icon={<UserDeleteOutlined style={{ color: 'red' }} />}
          disabled={!canUnassign || isUnassigning}
        >
          <Tooltip
            title={
              canUnassign
                ? 'Desasignar driver'
                : driverHasShipment(record)
                  ? 'La acción del envío ya está completada'
                  : 'El driver no tiene este envío'
            }
          >
            {loading ? (
              <LoadingOutlined style={{ color: 'grey', fontSize: 18 }} />
            ) : (
              <UserDeleteOutlined
                style={
                  canUnassign
                    ? { color: 'red', fontSize: 18 }
                    : { color: 'darkgray', fontSize: 18, cursor: 'not-allowed' }
                }
              />
            )}
          </Tooltip>
        </Popconfirm>
      );
    },
    width: 50,
  };

  const dataColums =
    waypointId === -1
      ? [externalCodeColumn, statusColumn, sizeColumn]
      : state === JourneyState.Draft
        ? [
            externalCodeColumn,
            statusColumn,
            actionStatusColumn,
            sizeColumn,
            deleteColumn,
          ]
        : state === JourneyState.Finished
          ? [
              externalCodeColumn,
              statusColumn,
              actionStatusColumn,
              sizeColumn,
              unassignDriverColumn,
            ]
          : [externalCodeColumn, statusColumn, actionStatusColumn, sizeColumn];

  return (
    <>
      <Table
        style={{
          padding: '0 0 4px 8px',
          background: '#0DB8B2',
        }}
        rowClassName={classes.shipmentRow}
        size="small"
        dataSource={shipments}
        columns={dataColums}
        pagination={false}
      />
      {collectionsTable}
    </>
  );
};

const WaypointsList = React.forwardRef<HTMLDivElement>((props, ref) => {
  const [filterMode, setFilterMode] = useState<'all' | 'noGeo' | 'issues'>(
    'all',
  );
  const [search, setSearch] = useState<string>('');
  const { token } = theme.useToken();
  const {
    waypoints,
    shipmentsById,
    collectionsById,
    deliveryByCollection,
    shipmentsWithNoActions,
    loading,
    setCenter,
    refetch,
    originAddress,
    destinationAddress,
    kind,
  } = useJourneyDashboardContext();

  const addressColumn = {
    title: 'Parada',
    render: (record: TableWaypoint) => {
      let style: React.CSSProperties = {};
      const issue = getWaypointIssue(record);
      if (!record.latitude || !record.longitude) {
        style.color = '#E49273';
      }
      if (issue) {
        style.color = issueColor(issue.blame);
      }
      // La fila "En retorno a despacho" de siempre; la salida y el regreso van como las demás.
      if (record.id === -1 && !record.endpoint) {
        style.color = token.colorPrimary;
        style.fontStyle = 'italic';
      }
      if (record.canceled) {
        style.textDecoration = 'line-through';
        style.color = 'darkgray';
      }
      return (
        <Flex align="center" justify="space-between" gap={8}>
          <b style={{ ...style, ...ELLIPSIS }}>{record.address}</b>
          {issue &&
            (() => {
              const blameLabel: Record<string, string> = {
                shipment: 'Fuera de hora',
                schedule: 'Fuera de hora',
                group: 'Conflicto',
                data: 'Sin geo',
              };
              const label = blameLabel[issue?.blame ?? ''] ?? 'Problema';
              const lateText =
                label === 'Fuera de hora'
                  ? lateCollectionText(record)
                  : undefined;
              const tooltip =
                lateText ??
                (issue?.reasonDescription
                  ? `${issue.reasonDescription}${
                      issue.blame !== 'data' && issue.exceededBySeconds
                        ? ` (+${issueMinutes(issue)} min)`
                        : ''
                    }`
                  : 'Problema no especificado');
              return (
                <Tooltip title={tooltip}>
                  <Tag
                    color={issueColor(issue?.blame)}
                    style={{ flexShrink: 0, marginInlineEnd: 0 }}
                  >
                    {label}
                  </Tag>
                </Tooltip>
              );
            })()}
        </Flex>
      );
    },
    ellipsis: true,
  };

  const centerColumn = {
    width: 50,
    align: 'center',
    render: (record: TableWaypoint) =>
      record.id !== -1 &&
      (record.latitude && record.longitude ? (
        <Tooltip title="Buscar en mapa">
          <CompassOutlined
            disabled={record.canceled}
            style={
              record.canceled
                ? {
                    textDecorationLine: 'line-through',
                    cursor: 'not-allowed',
                    color: 'darkgray',
                    fontSize: 18,
                  }
                : { fontSize: 18 }
            }
            onClick={() => {
              if (setCenter && !record.canceled)
                setCenter((prev) => {
                  const latitude =
                    prev?.lat !== record.latitude ? (record.latitude ?? 0) : 0;
                  const longitude =
                    prev?.lng !== record.longitude
                      ? (record.longitude ?? 0)
                      : 0;
                  return {
                    lat: latitude,
                    lng: longitude,
                  };
                });
            }}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Sin geolocalizar">
          <WarningOutlined style={{ fontSize: 18, color: '#E49273' }} />
        </Tooltip>
      )),
  };

  const noActionsWaypoint: TableWaypoint | undefined = useMemo(() => {
    if (shipmentsWithNoActions && shipmentsWithNoActions.length > 0) {
      return {
        id: -1,
        key: waypoints ? waypoints.length + 1 : 0,
        address: 'En retorno a despacho',
        shipments: shipmentsWithNoActions.map((shipment) => ({
          ...shipment,
          actionType: ActionType.Return,
          actionStatus: ActionStatus.Forced_end,
          statusDescription: 'Pendiente',
        })),
        canceled: false,
      };
    }
    return undefined;
  }, [shipmentsWithNoActions, waypoints]);

  // La parada final se llama como el centro: es el PaQuery Point del operador.
  const paquerypoint = useSelector(authPaquerypointSelector);
  const centerName =
    paquerypoint?.detail ?? paquerypoint?.name ?? 'Centro de distribución';

  // La salida, con la dirección del lugar como cualquier parada. Si la ruta sale del centro y
  // el origen no trae dirección, es la de la parada del centro; si sale de la primera parada,
  // es la de esa parada.
  const startAddress = (() => {
    const centerAddress = waypoints?.find((w) => w.centerDeliveries)?.address;
    switch (originAddress?.label) {
      case GeolocationType.CustomAddress:
        return originAddress.address ?? '';
      case GeolocationType.DriverLocation:
        return 'Donde esté el driver';
      case GeolocationType.Waypoint:
        return waypoints?.[0]?.address ?? '';
      default:
        // El centro de distribución, con o sin etiqueta. Nunca la primera parada.
        return [centerName, originAddress?.address || centerAddress]
          .filter(Boolean)
          .join(' · ');
    }
  })();
  // La salida de la ruta, como primera fila de la lista: se expande como las demás, pero
  // no tiene acciones. El regreso es la parada del centro, que ya viene en la lista.
  // El origen puede venir sin coordenadas (0,0): entonces son las del centro, o las de la
  // primera parada si la ruta arranca ahí.
  const startRow = useMemo(() => {
    const fallback =
      originAddress?.label === GeolocationType.Waypoint
        ? waypoints?.[0]
        : (waypoints?.find((w) => w.centerDeliveries) ?? destinationAddress);
    return {
      id: -2,
      key: 'start',
      address: ['Inicio del recorrido', startAddress]
        .filter(Boolean)
        .join(' · '),
      latitude: originAddress?.latitude || fallback?.latitude,
      longitude: originAddress?.longitude || fallback?.longitude,
      shipments: [],
      collections: [],
      canceled: false,
      endpoint: 'start',
    } as TableWaypoint;
  }, [originAddress, startAddress, waypoints, destinationAddress]);
  // El regreso, cuando la ruta no tiene la parada del centro (envíos): sale del destino
  // guardado en la ruta, que es el centro salvo que se haya cambiado.
  const endRow = useMemo(() => {
    if (!destinationAddress || waypoints?.some((w) => w.centerDeliveries)) {
      return undefined;
    }
    const place =
      destinationAddress.label === GeolocationType.CustomAddress
        ? (destinationAddress.address ?? '')
        : [centerName, destinationAddress.address].filter(Boolean).join(' · ');
    return {
      id: -3,
      key: 'end',
      address: ['Final del recorrido', place].filter(Boolean).join(' · '),
      latitude: destinationAddress.latitude || originAddress?.latitude,
      longitude: destinationAddress.longitude || originAddress?.longitude,
      shipments: [],
      collections: [],
      canceled: false,
      endpoint: 'end',
    } as TableWaypoint;
  }, [destinationAddress, originAddress, waypoints, centerName]);
  const parsedWaypoints = useMemo(() => {
    if (!waypoints || waypoints.length === 0) return [];
    return [
      // La salida es la primera fila, con la misma estructura que una parada.
      startRow,
      ...waypoints.map((waypoint) => {
        const waypointCollections: TableCollection[] = (waypoint.actions ?? [])
          .filter((action: Action) => !!action.collectionId)
          .map((action: Action) => ({
            collectionId: action.collectionId as number,
            collection: collectionsById[action.collectionId as number],
            actionStatus: action.status,
            deliveryStatus:
              deliveryByCollection[action.collectionId as number]?.status,
            transferredTo:
              action.transferredTo ??
              deliveryByCollection[action.collectionId as number]
                ?.transferredTo,
            arrivedAt: action.arrivedAt,
            issue: action.issue,
            collectionStatus:
              collectionsById[action.collectionId as number]?.collectionStatus,
          }));
        const waypointShipments =
          waypoint.actions
            ?.filter((action: Action) => !action.collectionId)
            .map((action: Action) => {
              const routeShipment = action.routeShipmentId
                ? shipmentsById[action.routeShipmentId]
                : undefined;
              return {
                ...routeShipment,
                actionType: action.actionType,
                actionStatus: action.status,
                issue: routeShipment?.issue ?? action.issue,
              } as TableShipment;
            }) ?? [];
        const waypointActionStatuses = [
          ...waypointShipments.map((row) => row.actionStatus),
          ...waypointCollections.map((row) => row.actionStatus),
        ];

        // El título de la parada de colecta lleva el nombre de la tienda y su dirección.
        // Una parada que mezcla envíos queda solo con la dirección, como siempre.
        const storeNames = Array.from(
          new Set(
            waypointCollections
              .map((row) => row.collection?.storeName)
              .filter((name): name is string => !!name),
          ),
        );
        let stopTitle = waypoint.address;
        if (waypoint.centerDeliveries) {
          // La última fila: el regreso al centro.
          stopTitle = `Final del recorrido · ${centerName}${
            waypoint.address ? ` · ${waypoint.address}` : ''
          }`;
        } else if (storeNames.length > 0 && waypointShipments.length === 0) {
          stopTitle = `${storeNames.join(' / ')} · ${waypoint.address ?? ''}`;
        }

        return {
          ...waypoint,
          address: stopTitle,
          endpoint: waypoint.centerDeliveries ? 'end' : undefined,
          hasIssues: !!getWaypointIssue(waypoint),
          key: waypoint.order || waypoint.id,
          shipments: waypointShipments,
          collections: waypointCollections,
          // Tachada solo si hay algo en la parada y todo está cancelado. Se miran las
          // dos listas, y la parada sin nada no cuenta: con una sola lista, una parada
          // que solo tiene colectas se quedaba sin acciones que mirar y "todas
          // canceladas" sobre una lista vacía da verdadero.
          canceled:
            waypointActionStatuses.length > 0 &&
            waypointActionStatuses.every(
              (status) => status === ActionStatus.Canceled,
            ),
        } as TableWaypoint;
      }),
      ...(endRow ? [endRow] : []),
      ...(filterMode !== 'all' ? [] : [noActionsWaypoint]),
    ]
      .filter((p) => {
        if (!p) return false;
        if (filterMode === 'all') return true;
        if (filterMode === 'noGeo') return !p.latitude || !p.longitude;
        if (filterMode === 'issues') return !!getWaypointIssue(p);
        return true;
      })
      .filter(Boolean) as TableWaypoint[];
  }, [
    waypoints,
    search,
    shipmentsById,
    collectionsById,
    deliveryByCollection,
    centerName,
    startRow,
    endRow,
    filterMode,
    noActionsWaypoint,
  ]);

  // El buscador dice lo que lleva la ruta, por su tipo (un borrador de colectas recién creado
  // todavía no tiene ninguna): en colectas no hay TN, hay tiendas; el título de la parada lleva
  // el nombre de la tienda, así que el filtro ya las encuentra.
  const isCollectionRoute = kind === 'collections';
  const hasShipments = Object.keys(shipmentsById ?? {}).length > 0;
  const searchPlaceholder =
    isCollectionRoute && hasShipments
      ? 'Dirección, tienda o TN'
      : isCollectionRoute
        ? 'Dirección o tienda'
        : 'Dirección o TN';
  const filteredWaypoints = useMemo(() => {
    if (!search) return parsedWaypoints;

    return (
      (parsedWaypoints
        .map((waypoint) => {
          if (!waypoint) return {};
          const addressMatch = waypoint.address
            ?.toLowerCase()
            .includes(search.toLowerCase());
          const matchingShipments = waypoint.shipments?.filter((shipment) =>
            shipment.externalCode?.toLowerCase().includes(search.toLowerCase()),
          );

          if (addressMatch) {
            return waypoint;
          } else if (matchingShipments && matchingShipments.length > 0) {
            return { ...waypoint, shipments: matchingShipments };
          }
          return {}; // No match
        })
        .filter(
          (waypoint) => Object.entries(waypoint).length > 0,
        ) as TableWaypoint[]) ?? []
    );
  }, [parsedWaypoints, search]);

  return (
    <div
      id="route-table"
      style={{
        height: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
      ref={ref as Ref<HTMLDivElement>}
    >
      <PaqueryTable
        paginate="virtual"
        tableHeight={550}
        loading={loading}
        fetching={loading}
        header={{
          refresh: () => refetch(),
          showElementCount: false,
          searcher: {
            key: 'searchCodeOrDestinationName',
            onSearching: (value: string) => {
              setSearch(value);
            },
            placeholder: searchPlaceholder,
            allowEmptySearch: true,
          },
          extraMainAction: (
            <ConfigProvider theme={{ token: { colorPrimary: '#E49273' } }}>
              <Flex align="center" style={{ height: '100%' }}>
                <Segmented
                  size="small"
                  value={filterMode}
                  onChange={(val) =>
                    setFilterMode(val as 'all' | 'noGeo' | 'issues')
                  }
                  style={{ color: 'white', background: token.colorPrimary }}
                  options={[
                    { label: 'Todos', value: 'all' },
                    { label: 'Sin geo', value: 'noGeo' },
                    { label: 'Fuera de hora', value: 'issues' },
                  ]}
                />
              </Flex>
            </ConfigProvider>
          ),
        }}
        dataSource={filteredWaypoints}
        dataColumns={[addressColumn, centerColumn]}
        colsForLargeDevice={[addressColumn, centerColumn]}
        usePackageRowColors
        content={{
          element: ActionsTable,
          expand: 'fixed',
        }}
      />
    </div>
  );
});

export default WaypointsList;
